/* eslint-disable */

import { useEffect, useState } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import { Menubar, MenubarContent, MenubarItem, MenubarMenu, MenubarTrigger } from '../ui/menubar'
import useMediaQuery from '../../hooks/useMediaQuery'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../ui/sheet'
import { Separator } from '../ui/separator'
import { toCurrency } from '../../utils/CurrencyUtil'
import { addNumberSuffix, capitalizeWords, monthShortNames, sanitizeName } from '../../utils/Util'
import env from '../../env/env'
import { post } from '../../http/restservice'
import { userDetailsState } from '../../Pages/LoginState'
import { useRecoilState } from 'recoil'
import { useModal } from '../../hooks/use-modal-store'
import { ChevronRight } from 'lucide-react'
import { Button } from '../ui/button'
import { useErrorPopup } from '../../hooks/useErrorPopup'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs2'
import SIPsEmpty from '../EmptyState/SIPsEmpty'
import { Skeleton } from '../ui/skeleton'
import {
  getSipStatusLabel,
  isActiveSipStatus,
  isInactiveSipStatus,
} from '../../utils/SipStatusUtil'

const INACTIVE_PAGE_SIZE = 5

const parseSipDate = (dateString) => {
  if (!dateString) return 0

  const normalizedDate = String(dateString).trim().replace('T', ' ')
  const [datePart] = normalizedDate.split(/\s+/)
  const delimiter = datePart.includes('/') ? '/' : '-'
  const parts = datePart.split(delimiter).map(Number)

  if (parts.length === 3 && parts.every((part) => !Number.isNaN(part))) {
    const [first, second, third] = parts
    const [year, month, day] =
      String(first).length === 4 ? [first, second, third] : [third, second, first]
    return new Date(year, month - 1, day).getTime()
  }

  const parsedDate = Date.parse(dateString)
  return Number.isNaN(parsedDate) ? 0 : parsedDate
}

const isOlderThanSixMonths = (dateString) => {
  const sipTime = parseSipDate(dateString)
  if (!sipTime) return false

  const sixMonthsAgo = new Date()
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)
  return sipTime < sixMonthsAgo.getTime()
}

const SipTable = ({ sips }) => {
  const [isHovered, setIsHovered] = useState(false)
  const [hoveredRow, setHoveredRow] = useState(null)
  const [activeSips, setActiveSips] = useState([])
  const [inProgressSips, setInProgressSips] = useState([])
  const [cancelledSips, setCancelledSips] = useState([])
  const [visibleOlderInactiveCount, setVisibleOlderInactiveCount] = useState(0)
  const [loadingMoreInactive, setLoadingMoreInactive] = useState(false)

  const [userDetails] = useRecoilState(userDetailsState)
  const { onOpen } = useModal()
  const { showError } = useErrorPopup()
  const clientCode = userDetails?.uid?.split('-')[0]
  const isDesktop = useMediaQuery('(min-width: 1024px)')

  useEffect(() => {
    if (sips?.length > 0) {
      setActiveSips(
        sips?.filter(
          (sip) => sip.customerBasketInvestmentId == null && isActiveSipStatus(sip?.sipStatus),
        ),
      )
      setInProgressSips(
        sips?.filter(
          (sip) => sip.customerBasketInvestmentId == null && sip?.sipStatus === 'IN_PROGRESS',
        ),
      )
      setCancelledSips(
        sips
          ?.filter(
            (sip) => sip.customerBasketInvestmentId == null && isInactiveSipStatus(sip?.sipStatus),
          )
          ?.sort((a, b) => parseSipDate(b?.sipStartDate) - parseSipDate(a?.sipStartDate)),
      )
      setVisibleOlderInactiveCount(0)
    }
  }, [sips])

  const getDate = (dateString) => {
    if (!dateString) return null // Handle null/undefined cases

    // Determine the correct delimiter
    const delimiter = dateString.includes('/') ? '/' : '-'

    return dateString.split(delimiter)[0]
  }

  const getMonth = (dateString) => {
    if (!dateString) return null // Handle null/undefined cases

    // Determine the correct delimiter
    const delimiter = dateString.includes('/') ? '/' : '-'

    return dateString.split(delimiter)[1]
  }

  const getYear = (dateString) => {
    if (!dateString) return null // Handle null/undefined cases

    // Determine the correct delimiter
    const delimiter = dateString.includes('/') ? '/' : '-'

    return dateString.split(delimiter)[2]
  }

  const handlePauseSIP = async (regNo) => {
    try {
      const payload = {
        clientCode: clientCode,
        regNo: regNo,
      }
      const response = await post(
        `${env.API_URL}mutualfundservice/api/bseStar/api/XSIPCancellation`,
        payload,
      )

      if (response.isError) {
        showError(
          'SIP cancellation failed',
          response?.response?.data?.BSERemarks?.toLowerCase(),
          () => {},
        )
      }

      if (
        response?.status === 'SUCCESS' &&
        response?.data?.BSERemarks?.includes('CANCELLATION INITIATED. KINDLY CONFIRM')
      ) {
        onOpen('openPopup', {
          title: 'Cancellation Initiated!',
          message:
            'Your cancellation request has been initiated. Please check your email and follow the instructions to complete the cancellation process.',
          severity: 'info',
        })
      }

      console.log('🚀 ~ handlePauseSIP ~ response:', response)
    } catch (error) {
      console.error('Request failed:', error)
      return []
    }
  }

  if (sips?.length === 0) {
    return (
      <SIPsEmpty
        title='It seems you have no SIPs'
        description='Get started with investing today. You can either invest in a plan or explore our custom funds.'
        mainText='SIPs'
        page='/dashboard/mutual-funds'
        showButton={false}
        variant='active'
      />
    )
  }

  const getSipsInfo = (sips = []) => {
    return sips
      ?.filter((sip) => sip.customerBasketInvestmentId == null)
      ?.reduce(
        (acc, item) => {
          if (isActiveSipStatus(item.sipStatus)) {
            acc.totalAmount += Number(item.installmentAmt) || 0
            acc.totalSIPCount += 1
          }
          return acc
        },
        { totalAmount: 0, totalSIPCount: 0 },
      )
  }

  const sortedCancelledSips = [...(cancelledSips || [])].sort(
    (a, b) => parseSipDate(b?.sipStartDate) - parseSipDate(a?.sipStartDate),
  )
  const recentInactiveSips = sortedCancelledSips.filter(
    (sip) => !isOlderThanSixMonths(sip?.sipStartDate),
  )
  const olderInactiveSips = sortedCancelledSips.filter((sip) =>
    isOlderThanSixMonths(sip?.sipStartDate),
  )
  const initialOlderInactiveCount = recentInactiveSips.length > 0 ? 0 : INACTIVE_PAGE_SIZE
  const effectiveOlderInactiveCount = Math.max(
    visibleOlderInactiveCount,
    initialOlderInactiveCount,
  )
  const visibleCancelledSips = [
    ...recentInactiveSips,
    ...olderInactiveSips.slice(0, effectiveOlderInactiveCount),
  ]
  const hasMoreOlderInactive = effectiveOlderInactiveCount < olderInactiveSips.length

  const handleLoadMoreInactive = () => {
    if (loadingMoreInactive || !hasMoreOlderInactive) return

    setLoadingMoreInactive(true)
    window.setTimeout(() => {
      setVisibleOlderInactiveCount((prev) => {
        const current = Math.max(prev, initialOlderInactiveCount)
        return Math.min(current + INACTIVE_PAGE_SIZE, olderInactiveSips.length)
      })
      setLoadingMoreInactive(false)
    }, 350)
  }

  const renderInactiveLoadMore = () => {
    if (!hasMoreOlderInactive) return null

    return (
      <div className='px-4 py-6 lg:px-0'>
        {loadingMoreInactive && (
          <div className='mb-4 space-y-3' aria-hidden='true'>
            {[1, 2].map((item) => (
              <div key={item} className='flex items-center justify-between p-4'>
                <div className='flex items-center gap-4'>
                  <Skeleton className='h-12 w-12 rounded-[8px]' />
                  <div className='space-y-2'>
                    <Skeleton className='h-4 w-48' />
                    <Skeleton className='h-4 w-20' />
                  </div>
                </div>
                <Skeleton className='h-9 w-20 rounded-md' />
              </div>
            ))}
          </div>
        )}
        <div className='flex justify-center'>
          <Button
            variant='outline'
            className='min-w-[140px]'
            loading={loadingMoreInactive}
            onClick={handleLoadMoreInactive}
          >
            Load more
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className='px-4 lg:px-0'>
        <div className='mt-4 w-full rounded-lg border border-black-100 bg-white px-4 py-4 shadow-[0_8px_24px_rgba(4,3,19,0.05)] lg:mx-auto lg:max-w-2xl lg:px-6'>
          <div className='grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4 sm:gap-6'>
            <div className='min-w-0 space-y-1'>
              <p className='truncate text-base font-semibold text-black-500 sm:text-lg'>
                {getSipsInfo(sips)?.totalSIPCount}
              </p>

              <p className='text-xs font-medium leading-4 text-black-400 sm:text-sm'>
                No. of active SIPs
              </p>
            </div>
            <Separator orientation='vertical' className='h-11' />

            <div className='min-w-0 space-y-1 text-right'>
              <p className='truncate text-base font-semibold text-black-500 sm:text-lg'>
                ₹ {toCurrency(getSipsInfo(sips)?.totalAmount)}
              </p>

              <p className='text-xs font-medium leading-4 text-black-400 sm:text-sm'>
                Active SIP amount
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className='flex justify-center items-center w-full flex-col gap-4'>
        <Tabs defaultValue='active' className='w-full'>
          <div className='flex items-center justify-center px-4 py-6 lg:px-0'>
            <TabsList className='grid h-11 w-full max-w-[420px] grid-cols-3 rounded-full md:w-auto'>
              <TabsTrigger
                value='active'
                className='h-9 px-1.5 text-[10px] leading-4 sm:text-sm md:px-4'
              >
                Active ({activeSips?.length})
              </TabsTrigger>
              <TabsTrigger
                value='in-progress'
                className='h-9 px-1.5 text-[10px] leading-4 sm:text-sm md:px-4'
              >
                In-Progress ({inProgressSips?.length})
              </TabsTrigger>
              <TabsTrigger
                value='cancelled'
                className='h-9 px-1.5 text-[10px] leading-4 sm:text-sm md:px-4'
              >
                Inactive ({cancelledSips?.length})
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value='active' className='w-full'>
            {activeSips?.length > 0 ? (
              isDesktop ? (
                <Table>
                  <TableHeader className='bg-[#F4F7FE]'>
                    <TableRow className='border-none'>
                      <TableHead className='rounded-l-xl text-center font-DMSans font-medium text-sm text-primary-400 w-18 px-0'></TableHead>
                      <TableHead className='font-DMSans font-medium text-sm text-primary-400'>
                        Investment Name
                      </TableHead>
                      <TableHead className='text-right font-DMSans font-medium text-sm text-primary-400'>
                        Investment amount
                      </TableHead>
                      <TableHead className='text-right rounded-r-xl'></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeSips &&
                      activeSips
                        ?.sort((a, b) => {
                          const dateA = +getDate(a.sipStartDate)
                          const dateB = +getDate(b.sipStartDate)
                          return dateA - dateB
                        })
                        ?.map((sips, index) => (
                          <TableRow
                            key={sips.date}
                            onMouseEnter={() => {
                              setIsHovered(true)
                              setHoveredRow(index)
                            }}
                            onMouseLeave={() => {
                              setIsHovered(false)
                              setHoveredRow(null)
                            }}
                            style={{
                              boxShadow:
                                isHovered &&
                                hoveredRow === index &&
                                '0px 24px 24px -20px rgba(0, 0, 0, 0.08)',
                            }}
                          >
                            <TableCell className='font-medium p-0 m-0'>
                              <div className='flex justify-center items-center'>
                                <img
                                  src={`${env.MF_LOGOS_URL}/${sips?.amcCode}.png`}
                                  className='h-12 w-12 rounded-[8px]'
                                />
                              </div>
                            </TableCell>

                            <TableCell>
                              <p className='font-DMSans font-semibold text-base text-black-500 flex items-center flex-nowrap gap-x-2'>
                                <span> {sanitizeName(sips)}</span>
                              </p>
                              <p className='text-sm text-black-400'>
                                Repeats on the{' '}
                                <strong>{addNumberSuffix(getDate(sips?.sipStartDate))}</strong> of
                                every month
                              </p>
                              {/* <p className='text-xs'>MandateID - {sips?.mandateId}</p> */}
                              <div className='flex gap-x-1 text-xs items-center text-black-400'>
                                Start Date -
                                <p className='flex justify-start text-xs items-center gap-x-1 text-center font-DMSans'>
                                  <span className='font-semibold'>
                                    {getDate(sips?.sipStartDate)}
                                  </span>
                                  <span className='font-semibold '>
                                    {monthShortNames[getMonth(sips?.sipStartDate) - 1]}
                                  </span>
                                  <span className='font-semibold '>
                                    {getYear(sips?.sipStartDate)}
                                  </span>
                                </p>
                              </div>
                            </TableCell>
                            {/* <TableCell className='text-right'>+{sips?.Annual_Step_Up}%</TableCell> */}
                            <TableCell className='text-right font-DMSans font-semibold text-base'>
                              ₹ {toCurrency(sips?.installmentAmt)}
                            </TableCell>
                            {capitalizeWords(sips?.sipStatus) === 'Active' && (
                              <TableCell className='text-right'>
                                <Menubar>
                                  <MenubarMenu className='bg-transparent'>
                                    <MenubarTrigger className='hover:bg-[#F4F7FE] px-1 py-[10px] rounded-full h-10 w-7 flex justify-center items-center cursor-pointer'>
                                      <MoreVertIcon sx={{ color: '#41558D' }} />
                                    </MenubarTrigger>

                                    <MenubarContent className='rounded-[10px] flex flex-col gap-y-1'>
                                      {/* <MenubarItem
                            onClick={() => setOpen(true)}
                            className='py-2 pl-3 text-sm text-black-500 cursor-pointer  hover:bg-blue-100'
                          >
                            Modify
                          </MenubarItem> */}

                                      <MenubarItem
                                        className='py-2 pl-3 text-sm text-black-500 cursor-pointer hover:bg-blue-100'
                                        onClick={() => handlePauseSIP(sips?.sipRegId)}
                                      >
                                        Cancel
                                      </MenubarItem>
                                      {/* 
                          <MenubarItem
                            onClick={() => {
                              setOpen(false)
                            }}
                            className='py-2 pl-3 text-sm cursor-pointer hover:bg-red-100 hover:text-red-500'
                          >
                            Cancel
                          </MenubarItem> */}
                                    </MenubarContent>
                                  </MenubarMenu>
                                </Menubar>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                  </TableBody>
                </Table>
              ) : (
                <>
                  <div className='px-4 fabits-compact-card-stack'>
                    {/* activeSips */}

                    {activeSips &&
                      activeSips
                        ?.sort((a, b) => {
                          const dateA = +getDate(a.sipStartDate)
                          const dateB = +getDate(b.sipStartDate)
                          return dateA - dateB
                        })
                        .map((sips, index) => (
                          <div
                            key={sips.date}
                            onMouseEnter={() => {
                              setIsHovered(true)
                              setHoveredRow(index)
                            }}
                            onMouseLeave={() => {
                              setIsHovered(false)
                              setHoveredRow(null)
                            }}
                            style={{
                              boxShadow:
                                isHovered &&
                                hoveredRow === index &&
                                '0px 24px 24px -20px rgba(0, 0, 0, 0.08)',
                            }}
                            className='flex gap-x-4'
                          >
                            <div className='font-medium text-center'>
                              {/* <div className='h-12 w-12 bg-primary-50 rounded-lg flex justify-center items-center'>
                      <div className='text-center font-DMSans text-primary-500'>
                        <p className='font-semibold text-[18px] leading-[26px]'>
                          {getDate(sips?.sipStartDate)}
                        </p>
                        <p className='font-medium text-[10px] leading-3'>
                          {monthShortNames[getMonth(sips?.sipStartDate) - 1]}
                        </p>
                      </div>
                    </div> */}
                              <img
                                src={`${env.MF_LOGOS_URL}/${sips?.amcCode}.png`}
                                className='h-10 w-10 mt-1 rounded-[8px]'
                              />
                            </div>

                            <div className='w-full'>
                              <div className='w-full flex justify-between'>
                                <div>
                                  <div className='flex items-center'>
                                    <p className='font-DMSans font-semibold text-base truncate whitespace-nowrap overflow-hidden max-w-[200px] md:max-w-max'>
                                      {sanitizeName(sips)}
                                    </p>
                                  </div>
                                  <p className='text-sm text-black-400'>
                                    Repeats on the{' '}
                                    <strong className='text-black-500'>
                                      {addNumberSuffix(getDate(sips?.sipStartDate))}
                                    </strong>{' '}
                                    of every month
                                  </p>
                                </div>

                                {capitalizeWords(sips?.sipStatus) === 'Active' && (
                                  <Sheet>
                                    <SheetTrigger className='px-1 py-[10px] rounded-full h-6 w-6 flex justify-center items-center'>
                                      <ChevronRight className='h-4 w-4' />
                                    </SheetTrigger>
                                    <SheetContent side='bottom' className='rounded-t-3xl'>
                                      <SheetHeader>
                                        <SheetTitle>
                                          <div className='flex items-center gap-x-1'>
                                            <div className='flex justify-center items-center'>
                                              <img
                                                src={`${env.MF_LOGOS_URL}/${sips?.amcCode}.png`}
                                                className='h-10 w-10 rounded-[8px]'
                                              />
                                            </div>
                                            <div className='text-start'>
                                              <p className='font-DMSans font-semibold text-base text-black-500 mb-1'>
                                                {sips?.sname}
                                              </p>
                                              <span className='font-DMSans font-medium text-sm text-black-400 flex justify-between'>
                                                <p> Mandate ID - {sips?.mandateId}</p>
                                                <p className='text-black-500 font-semibold'>
                                                  ₹ {sips?.installmentAmt}
                                                </p>
                                              </span>
                                            </div>
                                          </div>
                                        </SheetTitle>
                                      </SheetHeader>
                                      <Separator className='my-2' />
                                      <div>
                                        {/* <div
                              onClick={handleModify}
                              className='flex  items-center gap-x-4 hover:bg-[#F4F7FE] p-[10px] rounded-lg'
                            >
                              <EditIcon sx={{ height: '16px', width: '16px' }} />
                              <p className='font-DMSans font-medium text-sm'>Modify</p>
                            </div>
                            <div className='flex items-center gap-x-4  hover:bg-[#F4F7FE] p-[10px] rounded-lg'>
                              <PauseRoundedIcon sx={{ height: '16px', width: '16px' }} />
                              <p className='font-DMSans font-medium text-sm'>Pause</p>
                            </div> */}
                                        <Button
                                          onClick={() => handlePauseSIP(sips?.sipRegId)}
                                          className='flex items-center text-center gap-x-4 bg-error-600  hover:bg-red-500 p-[10px] rounded-lg w-full'
                                        >
                                          <CancelRoundedIcon
                                            sx={{ height: '16px', width: '16px' }}
                                          />
                                          <p className='font-DMSans font-medium text-sm'>Cancel</p>
                                        </Button>
                                      </div>
                                    </SheetContent>
                                  </Sheet>
                                )}
                              </div>
                              <div className='w-full flex justify-between items-center font-DMSans font-semibold'>
                                <p className='text-xs'>MandateID - {sips?.mandateId}</p>
                                <p className='text-base'>₹ {toCurrency(sips?.installmentAmt)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                  </div>
                </>
              )
            ) : (
              <SIPsEmpty
                title='There are no active SIPs right now.'
                description='Start your investment journey today by choosing a plan or exploring our curated funds.'
                mainText='Active SIPs'
                page='/dashboard/mutual-funds'
                showButton={true}
                variant='active'
              />
            )}
          </TabsContent>
          <TabsContent value='in-progress'>
            {inProgressSips?.length > 0 ? (
              isDesktop ? (
                <Table>
                  <TableHeader className='bg-[#F4F7FE]'>
                    <TableRow className='border-none'>
                      <TableHead className='rounded-l-xl text-center font-DMSans font-medium text-sm text-primary-400 w-18 px-0'></TableHead>
                      <TableHead className='font-DMSans font-medium text-sm text-primary-400'>
                        Investment Name
                      </TableHead>
                      <TableHead className='text-right font-DMSans font-medium text-sm text-primary-400'>
                        Investment amount
                      </TableHead>
                      <TableHead className='text-right rounded-r-xl'></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inProgressSips
                      ?.sort((a, b) => {
                        const dateA = +getDate(a.sipStartDate)
                        const dateB = +getDate(b.sipStartDate)
                        return dateA - dateB
                      })
                      ?.map((sips, index) => (
                        <TableRow
                          key={sips.date}
                          onMouseEnter={() => {
                            setIsHovered(true)
                            setHoveredRow(index)
                          }}
                          onMouseLeave={() => {
                            setIsHovered(false)
                            setHoveredRow(null)
                          }}
                          style={{
                            boxShadow:
                              isHovered &&
                              hoveredRow === index &&
                              '0px 24px 24px -20px rgba(0, 0, 0, 0.08)',
                          }}
                        >
                          <TableCell className='font-medium p-0 m-0'>
                            <div className='flex justify-center items-center'>
                              <img
                                src={`${env.MF_LOGOS_URL}/${sips?.amcCode}.png`}
                                className='h-12 w-12 rounded-[8px]'
                              />
                            </div>
                          </TableCell>

                          <TableCell>
                            <p className='font-DMSans font-semibold text-base text-black-500 flex items-center flex-nowrap gap-x-2'>
                              <span> {sanitizeName(sips)}</span>
                            </p>
                            <p className='text-sm text-black-400'>
                              Repeats on the{' '}
                              <strong>{addNumberSuffix(getDate(sips?.sipStartDate))}</strong> of
                              every month
                            </p>
                            {/* <p className='text-xs'>MandateID - {sips?.mandateId}</p> */}
                            <div className='flex gap-x-1 text-xs items-center text-black-400'>
                              Start Date -
                              <p className='flex justify-start text-xs items-center gap-x-1 text-center font-DMSans'>
                                <span className='font-semibold'>{getDate(sips?.sipStartDate)}</span>
                                <span className='font-semibold '>
                                  {monthShortNames[getMonth(sips?.sipStartDate) - 1]}
                                </span>
                                <span className='font-semibold '>
                                  {getYear(sips?.sipStartDate)}
                                </span>
                              </p>
                            </div>
                          </TableCell>
                          {/* <TableCell className='text-right'>+{sips?.Annual_Step_Up}%</TableCell> */}
                          <TableCell className='text-right font-DMSans font-semibold text-base'>
                            ₹ {toCurrency(sips?.installmentAmt)}
                          </TableCell>
                          {capitalizeWords(sips?.sipStatus) === 'Active' && (
                            <TableCell className='text-right'>
                              <Menubar>
                                <MenubarMenu className='bg-transparent'>
                                  <MenubarTrigger className='hover:bg-[#F4F7FE] px-1 py-[10px] rounded-full h-10 w-7 flex justify-center items-center cursor-pointer'>
                                    <MoreVertIcon sx={{ color: '#41558D' }} />
                                  </MenubarTrigger>

                                  <MenubarContent className='rounded-[10px] flex flex-col gap-y-1'>
                                    {/* <MenubarItem
                            onClick={() => setOpen(true)}
                            className='py-2 pl-3 text-sm text-black-500 cursor-pointer  hover:bg-blue-100'
                          >
                            Modify
                          </MenubarItem> */}

                                    <MenubarItem
                                      className='py-2 pl-3 text-sm text-black-500 cursor-pointer hover:bg-blue-100'
                                      onClick={() => handlePauseSIP(sips?.sipRegId)}
                                    >
                                      Cancel
                                    </MenubarItem>
                                    {/* 
                          <MenubarItem
                            onClick={() => {
                              setOpen(false)
                            }}
                            className='py-2 pl-3 text-sm cursor-pointer hover:bg-red-100 hover:text-red-500'
                          >
                            Cancel
                          </MenubarItem> */}
                                  </MenubarContent>
                                </MenubarMenu>
                              </Menubar>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              ) : (
                <>
                  <div className='px-4 fabits-compact-card-stack'>
                    {/* inProgressSips */}

                    {inProgressSips &&
                      inProgressSips
                        ?.sort((a, b) => {
                          const dateA = +getDate(a.sipStartDate)
                          const dateB = +getDate(b.sipStartDate)
                          return dateA - dateB
                        })
                        .map((sips, index) => (
                          <div
                            key={sips.date}
                            onMouseEnter={() => {
                              setIsHovered(true)
                              setHoveredRow(index)
                            }}
                            onMouseLeave={() => {
                              setIsHovered(false)
                              setHoveredRow(null)
                            }}
                            style={{
                              boxShadow:
                                isHovered &&
                                hoveredRow === index &&
                                '0px 24px 24px -20px rgba(0, 0, 0, 0.08)',
                            }}
                            className='flex gap-x-4'
                          >
                            <div className='font-medium text-center'>
                              {/* <div className='h-12 w-12 bg-primary-50 rounded-lg flex justify-center items-center'>
                      <div className='text-center font-DMSans text-primary-500'>
                        <p className='font-semibold text-[18px] leading-[26px]'>
                          {getDate(sips?.sipStartDate)}
                        </p>
                        <p className='font-medium text-[10px] leading-3'>
                          {monthShortNames[getMonth(sips?.sipStartDate) - 1]}
                        </p>
                      </div>
                    </div> */}
                              <img
                                src={`${env.MF_LOGOS_URL}/${sips?.amcCode}.png`}
                                className='h-10 w-10 mt-1 rounded-[8px]'
                              />
                            </div>

                            <div className='w-full'>
                              <div className='w-full flex justify-between'>
                                <div>
                                  <div className='flex items-center'>
                                    <p className='font-DMSans font-semibold text-base truncate whitespace-nowrap overflow-hidden max-w-[200px] md:max-w-max'>
                                      {sanitizeName(sips)}
                                    </p>
                                  </div>
                                  <p className='text-sm text-black-400'>
                                    Repeats on the{' '}
                                    <strong className='text-black-500'>
                                      {addNumberSuffix(getDate(sips?.sipStartDate))}
                                    </strong>{' '}
                                    of every month
                                  </p>
                                </div>

                                {capitalizeWords(sips?.sipStatus) === 'Active' && (
                                  <Sheet>
                                    <SheetTrigger className='px-1 py-[10px] rounded-full h-6 w-6 flex justify-center items-center'>
                                      <ChevronRight className='h-4 w-4' />
                                    </SheetTrigger>
                                    <SheetContent side='bottom' className='rounded-t-3xl'>
                                      <SheetHeader>
                                        <SheetTitle>
                                          <div className='flex items-center gap-x-1'>
                                            <div className='flex justify-center items-center'>
                                              <img
                                                src={`${env.MF_LOGOS_URL}/${sips?.amcCode}.png`}
                                                className='h-10 w-10 rounded-[8px]'
                                              />
                                            </div>
                                            <div className='text-start'>
                                              <p className='font-DMSans font-semibold text-base text-black-500 mb-1'>
                                                {sips?.sname}
                                              </p>
                                              <span className='font-DMSans font-medium text-sm text-black-400 flex justify-between'>
                                                <p> Mandate ID - {sips?.mandateId}</p>
                                                <p className='text-black-500 font-semibold'>
                                                  ₹ {sips?.installmentAmt}
                                                </p>
                                              </span>
                                            </div>
                                          </div>
                                        </SheetTitle>
                                      </SheetHeader>
                                      <Separator className='my-2' />
                                      <div>
                                        {/* <div
                              onClick={handleModify}
                              className='flex  items-center gap-x-4 hover:bg-[#F4F7FE] p-[10px] rounded-lg'
                            >
                              <EditIcon sx={{ height: '16px', width: '16px' }} />
                              <p className='font-DMSans font-medium text-sm'>Modify</p>
                            </div>
                            <div className='flex items-center gap-x-4  hover:bg-[#F4F7FE] p-[10px] rounded-lg'>
                              <PauseRoundedIcon sx={{ height: '16px', width: '16px' }} />
                              <p className='font-DMSans font-medium text-sm'>Pause</p>
                            </div> */}
                                        <Button
                                          onClick={() => handlePauseSIP(sips?.sipRegId)}
                                          className='flex items-center text-center gap-x-4 bg-error-600  hover:bg-red-500 p-[10px] rounded-lg w-full'
                                        >
                                          <CancelRoundedIcon
                                            sx={{ height: '16px', width: '16px' }}
                                          />
                                          <p className='font-DMSans font-medium text-sm'>Cancel</p>
                                        </Button>
                                      </div>
                                    </SheetContent>
                                  </Sheet>
                                )}
                              </div>
                              <div className='w-full flex justify-between items-center font-DMSans font-semibold'>
                                <p className='text-xs'>MandateID - {sips?.mandateId}</p>
                                <p className='text-base'>₹ {toCurrency(sips?.installmentAmt)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                  </div>
                </>
              )
            ) : (
              <SIPsEmpty
                title='There are no SIPs in progress right now.'
                description='Start your investment journey today by choosing a plan or exploring our curated funds.'
                mainText='SIPs'
                page='/dashboard/mutual-funds'
                showButton={true}
                variant='inProgress'
              />
            )}
          </TabsContent>

          <TabsContent value='cancelled'>
            {cancelledSips?.length > 0 ? (
              <>
              {isDesktop ? (
                <Table>
                  <TableHeader className='bg-[#F4F7FE]'>
                    <TableRow className='border-none'>
                      <TableHead className='rounded-l-xl text-center font-DMSans font-medium text-sm text-primary-400 w-18 px-0'></TableHead>
                      <TableHead className='font-DMSans font-medium text-sm text-primary-400'>
                        Investment Name
                      </TableHead>
                      <TableHead className='text-right font-DMSans font-medium text-sm text-primary-400'>
                        Investment amount
                      </TableHead>
                      <TableHead className='text-right rounded-r-xl'></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleCancelledSips.map((sips, index) => (
                          <TableRow
                            key={sips?.sipRegId || sips?.date || index}
                            onMouseEnter={() => {
                              setIsHovered(true)
                              setHoveredRow(index)
                            }}
                            onMouseLeave={() => {
                              setIsHovered(false)
                              setHoveredRow(null)
                            }}
                            style={{
                              boxShadow:
                                isHovered &&
                                hoveredRow === index &&
                                '0px 24px 24px -20px rgba(0, 0, 0, 0.08)',
                            }}
                          >
                            <TableCell className='font-medium p-0 m-0'>
                              <div className='flex justify-center items-center'>
                                <img
                                  src={`${env.MF_LOGOS_URL}/${sips?.amcCode}.png`}
                                  className='h-12 w-12 rounded-[8px]'
                                />
                              </div>
                            </TableCell>

                            <TableCell>
                              <p className='font-DMSans font-semibold text-base text-black-500 flex items-center flex-nowrap gap-x-2'>
                                <span> {sanitizeName(sips)}</span>
                                <span className='text-sm text-error-500 bg-error-50 rounded-full px-2'>
                                  {getSipStatusLabel(sips?.sipStatus)}
                                </span>
                              </p>
                              <p className='text-sm text-black-400'>
                                Repeats on the{' '}
                                <strong>{addNumberSuffix(getDate(sips?.sipStartDate))}</strong> of
                                every month
                              </p>
                              {/* <p className='text-xs'>MandateID - {sips?.mandateId}</p> */}
                              <div className='flex gap-x-1 text-xs items-center text-black-400'>
                                Start Date -
                                <p className='flex justify-start text-xs items-center gap-x-1 text-center font-DMSans'>
                                  <span className='font-semibold'>
                                    {getDate(sips?.sipStartDate)}
                                  </span>
                                  <span className='font-semibold '>
                                    {monthShortNames[getMonth(sips?.sipStartDate) - 1]}
                                  </span>
                                  <span className='font-semibold '>
                                    {getYear(sips?.sipStartDate)}
                                  </span>
                                </p>
                              </div>
                            </TableCell>
                            {/* <TableCell className='text-right'>+{sips?.Annual_Step_Up}%</TableCell> */}
                            <TableCell className='text-right font-DMSans font-semibold text-base'>
                              ₹ {toCurrency(sips?.installmentAmt)}
                            </TableCell>
                            {capitalizeWords(sips?.sipStatus) === 'Active' && (
                              <TableCell className='text-right'>
                                <Menubar>
                                  <MenubarMenu className='bg-transparent'>
                                    <MenubarTrigger className='hover:bg-[#F4F7FE] px-1 py-[10px] rounded-full h-10 w-7 flex justify-center items-center cursor-pointer'>
                                      <MoreVertIcon sx={{ color: '#41558D' }} />
                                    </MenubarTrigger>

                                    <MenubarContent className='rounded-[10px] flex flex-col gap-y-1'>
                                      {/* <MenubarItem
                            onClick={() => setOpen(true)}
                            className='py-2 pl-3 text-sm text-black-500 cursor-pointer  hover:bg-blue-100'
                          >
                            Modify
                          </MenubarItem> */}

                                      <MenubarItem
                                        className='py-2 pl-3 text-sm text-black-500 cursor-pointer hover:bg-blue-100'
                                        onClick={() => handlePauseSIP(sips?.sipRegId)}
                                      >
                                        Cancel
                                      </MenubarItem>
                                      {/* 
                          <MenubarItem
                            onClick={() => {
                              setOpen(false)
                            }}
                            className='py-2 pl-3 text-sm cursor-pointer hover:bg-red-100 hover:text-red-500'
                          >
                            Cancel
                          </MenubarItem> */}
                                    </MenubarContent>
                                  </MenubarMenu>
                                </Menubar>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                  </TableBody>
                </Table>
              ) : (
                <>
                  <div className='px-4 fabits-compact-card-stack'>
                    {/* inactiveSips */}

                    {visibleCancelledSips.map((sips, index) => (
                          <div
                            key={sips?.sipRegId || sips?.date || index}
                            onMouseEnter={() => {
                              setIsHovered(true)
                              setHoveredRow(index)
                            }}
                            onMouseLeave={() => {
                              setIsHovered(false)
                              setHoveredRow(null)
                            }}
                            style={{
                              boxShadow:
                                isHovered &&
                                hoveredRow === index &&
                                '0px 24px 24px -20px rgba(0, 0, 0, 0.08)',
                            }}
                            className='flex gap-x-4'
                          >
                            <div className='font-medium text-center'>
                              {/* <div className='h-12 w-12 bg-primary-50 rounded-lg flex justify-center items-center'>
                      <div className='text-center font-DMSans text-primary-500'>
                        <p className='font-semibold text-[18px] leading-[26px]'>
                          {getDate(sips?.sipStartDate)}
                        </p>
                        <p className='font-medium text-[10px] leading-3'>
                          {monthShortNames[getMonth(sips?.sipStartDate) - 1]}
                        </p>
                      </div>
                    </div> */}
                              <img
                                src={`${env.MF_LOGOS_URL}/${sips?.amcCode}.png`}
                                className='h-10 w-10 mt-1 rounded-[8px]'
                              />
                            </div>

                            <div className='w-full'>
                              <div className='w-full flex justify-between'>
                                <div>
                                  <div className='flex items-center'>
                                    <p className='font-DMSans font-semibold text-base truncate whitespace-nowrap overflow-hidden max-w-[200px] md:max-w-max'>
                                      {sanitizeName(sips)}
                                    </p>
                                    <span className='text-sm text-error-500 bg-error-50 rounded-full px-2'>
                                      {getSipStatusLabel(sips?.sipStatus)}
                                    </span>
                                  </div>
                                  <p className='text-sm text-black-400'>
                                    Repeats on the{' '}
                                    <strong className='text-black-500'>
                                      {addNumberSuffix(getDate(sips?.sipStartDate))}
                                    </strong>{' '}
                                    of every month
                                  </p>
                                </div>

                                {capitalizeWords(sips?.sipStatus) === 'Active' && (
                                  <Sheet>
                                    <SheetTrigger className='px-1 py-[10px] rounded-full h-6 w-6 flex justify-center items-center'>
                                      <ChevronRight className='h-4 w-4' />
                                    </SheetTrigger>
                                    <SheetContent side='bottom' className='rounded-t-3xl'>
                                      <SheetHeader>
                                        <SheetTitle>
                                          <div className='flex items-center gap-x-1'>
                                            <div className='flex justify-center items-center'>
                                              <img
                                                src={`${env.MF_LOGOS_URL}/${sips?.amcCode}.png`}
                                                className='h-10 w-10 rounded-[8px]'
                                              />
                                            </div>
                                            <div className='text-start'>
                                              <p className='font-DMSans font-semibold text-base text-black-500 mb-1'>
                                                {sips?.sname}
                                              </p>
                                              <span className='font-DMSans font-medium text-sm text-black-400 flex justify-between'>
                                                <p> Mandate ID - {sips?.mandateId}</p>
                                                <p className='text-black-500 font-semibold'>
                                                  ₹ {sips?.installmentAmt}
                                                </p>
                                              </span>
                                            </div>
                                          </div>
                                        </SheetTitle>
                                      </SheetHeader>
                                      <Separator className='my-2' />
                                      <div>
                                        {/* <div
                              onClick={handleModify}
                              className='flex  items-center gap-x-4 hover:bg-[#F4F7FE] p-[10px] rounded-lg'
                            >
                              <EditIcon sx={{ height: '16px', width: '16px' }} />
                              <p className='font-DMSans font-medium text-sm'>Modify</p>
                            </div>
                            <div className='flex items-center gap-x-4  hover:bg-[#F4F7FE] p-[10px] rounded-lg'>
                              <PauseRoundedIcon sx={{ height: '16px', width: '16px' }} />
                              <p className='font-DMSans font-medium text-sm'>Pause</p>
                            </div> */}
                                        <Button
                                          onClick={() => handlePauseSIP(sips?.sipRegId)}
                                          className='flex items-center text-center gap-x-4 bg-error-600  hover:bg-red-500 p-[10px] rounded-lg w-full'
                                        >
                                          <CancelRoundedIcon
                                            sx={{ height: '16px', width: '16px' }}
                                          />
                                          <p className='font-DMSans font-medium text-sm'>Cancel</p>
                                        </Button>
                                      </div>
                                    </SheetContent>
                                  </Sheet>
                                )}
                              </div>
                              <div className='w-full flex justify-between items-center font-DMSans font-semibold'>
                                <p className='text-xs'>MandateID - {sips?.mandateId}</p>
                                <p className='text-base'>₹ {toCurrency(sips?.installmentAmt)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                  </div>
                </>
              )}
              {renderInactiveLoadMore()}
              </>
            ) : (
              <SIPsEmpty
                title='There are no inactive SIPs right now.'
                description='Start your investment journey today by choosing a plan or exploring our curated funds.'
                mainText='Inactive SIPs'
                page='/dashboard/mutual-funds'
                showButton={true}
                variant='inactive'
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}

export default SipTable
